import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { SelectButton } from "primereact/selectbutton";
import { Skeleton } from "primereact/skeleton";
import { Tag } from "primereact/tag";
import { SeleccionarDestacadosDialog } from "../components/dialogs/SeleccionarDestacadosDialog";
import AppToast from "../../../shared/components/AppToast";
import { ensureCatalogAuth } from "../../../services/backend/authService";
import {
  getDestacadosConfig,
  editarDestacadosConfig,
} from "../../../services/backend/tenantConfigService";
import { getCompany } from "../../../config/companyConfig";
import { moverElementoEnString } from "../../../utils/orderUtils";

const OPCIONES_FALLBACK = [
  { label: "Más Vendidos", value: "masVendidos" },
  { label: "Recomendados", value: "recomendados" },
];

const CANALES_OPCIONES = [
  { label: "B2B", value: "B2B" },
  { label: "B2C", value: "B2C" },
];

const buildConfigFallback = (canalKey) => {
  const company = getCompany();
  const canalCfg = company.destacados?.[canalKey] || {};
  return {
    limite: canalCfg.limite,
    tipo: canalCfg.tipo,
    opciones: OPCIONES_FALLBACK,
  };
};

const extractCanalCfg = (destacados, canalKey) => {
  if (!destacados) return null;
  if (destacados[canalKey]) return destacados[canalKey];
  return destacados;
};

const Destacados = () => {
  const toastRef = useRef(null);
  const tenantId = getCompany().id;
  const [canal, setCanal] = useState(getCompany().defaultCanal);
  const [config, setConfig] = useState(null);
  const [draft, setDraft] = useState(null);
  const [opciones, setOpciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);

  const showToast = (severity, summary, detail) => {
    toastRef.current?.show({ severity, summary, detail, life: 4000 });
  };

  const cargarConfig = useCallback(async (canalParam) => {
    setLoading(true);
    setEditando(false);
    const ck = canalParam.toLowerCase();
    const fallback = buildConfigFallback(ck);
    try {
      await ensureCatalogAuth();
      const res = await getDestacadosConfig(tenantId, canalParam);
      if (res?.resultado) {
        const canalCfg = extractCanalCfg(res.destacados, ck);
        const cfg = {
          limite: canalCfg?.limite,
          tipo: canalCfg?.tipo,
          codigos: canalCfg?.codigos || "",
        };
        setConfig(cfg);
        setDraft(cfg);
        setOpciones(
          Array.isArray(res.tipoDestacadosOpciones) &&
            res.tipoDestacadosOpciones.length
            ? res.tipoDestacadosOpciones
            : fallback.opciones
        );
      } else {
        setConfig({ limite: fallback.limite, tipo: fallback.tipo, codigos: "" });
        setDraft({ limite: fallback.limite, tipo: fallback.tipo, codigos: "" });
        setOpciones(fallback.opciones);
      }
    } catch (err) {
      setConfig({ limite: fallback.limite, tipo: fallback.tipo, codigos: "" });
      setDraft({ limite: fallback.limite, tipo: fallback.tipo, codigos: "" });
      setOpciones(fallback.opciones);
      showToast(
        "error",
        "Error",
        err?.message || "No se pudo cargar la configuración."
      );
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (mounted) await cargarConfig(canal);
    };
    run();
    return () => {
      mounted = false;
    };
  }, [canal, cargarConfig]);

  const onCanalChange = (e) => {
    if (e.value && e.value !== canal) {
      setCanal(e.value);
    }
  };

  const onEditar = () => {
    setDraft(config);
    setEditando(true);
  };

  const onCancelar = () => {
    setDraft(config);
    setEditando(false);
  };

  const onGuardar = async () => {
    if (draft.limite == null) {
      showToast("warn", "Faltan datos", "El límite es obligatorio.");
      return;
    }
    if (draft.tipo == null) {
      showToast("warn", "Faltan datos", "El tipo es obligatorio.");
      return;
    }
    if (draft.tipo === "recomendados" && !draft.codigos) {
      showToast(
        "warn",
        "Faltan datos",
        "Debes seleccionar al menos un producto para el modo Recomendados."
      );
      return;
    }
    setGuardando(true);
    try {
      await ensureCatalogAuth();
      await editarDestacadosConfig({
        tenant_id: tenantId,
        canal,
        limite: draft.limite,
        tipo: draft.tipo,
        codigos: draft.tipo === "recomendados" ? draft.codigos : "",
      });
      await cargarConfig(canal);
      setEditando(false);
      showToast(
        "success",
        "Configuración guardada",
        "El límite y el tipo fueron actualizados."
      );
    } catch (err) {
      showToast(
        "error",
        "Error",
        err?.message || "No se pudo guardar la configuración."
      );
    } finally {
      setGuardando(false);
    }
  };

  const onCampoChange = (campo, valor) => {
    setDraft((prev) => ({ ...prev, [campo]: valor }));
  };

  const moverCodigo = (posicion, direccion) => {
    onCampoChange("codigos", moverElementoEnString(draft.codigos, posicion, direccion));
  };

  const tipoLabel =
    opciones.find((o) => o.value === config?.tipo)?.label || config?.tipo || "";

  return (
    <>
      <AppToast ref={toastRef} />
      <SeleccionarDestacadosDialog
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        onConfirm={(codigos) => onCampoChange("codigos", codigos)}
        codigosIniciales={draft?.codigos || ""}
        tipo={canal}
      />
      <div className="py-3">
        <div className="grid">
          <div className="col-12">
            <div className="COL-12">
              <h4 className="font-bold mb-0">
                Gestión de Productos Destacados
              </h4>
              <p className="text-700 text-lg line-height-3 mb-3">
                Configura y edita el límite y el tipo de productos mostrados (Más vendidos o recomendados) que se muestran en la sección de categorías del Home.
              </p>
            </div>
            <div className="grid">
              <div className="col-12 md:col-6">
                <div className="border-1 surface-border border-round p-4">
                  <div className="flex align-items-center justify-content-between mb-3">
                    <span className="text-xl font-semibold">
                      Configuración actual
                    </span>

                    <SelectButton
                      value={canal}
                      options={CANALES_OPCIONES}
                      onChange={onCanalChange}
                    />

                    {!editando && !loading && (
                      <Button
                        icon="pi pi-pencil"
                        label="Editar"
                        onClick={onEditar}
                      />
                    )}
                    
                  </div>

                  {loading ? (
                    <div className="flex flex-column gap-3">
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Tipo activo</span>
                        <Skeleton width="6rem" height="1.5rem" />
                      </div>
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Límite de productos</span>
                        <Skeleton width="4rem" height="1.5rem" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-column gap-3">
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Canal</span>
                        <Tag value={canal} severity="info" />
                      </div>
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Tipo activo</span>
                        <Tag value={tipoLabel} severity="info" />
                      </div>
                      <div className="flex align-items-center justify-content-between">
                        <span className="text-700">Límite de productos</span>
                        <Tag value={config.limite} />
                      </div>
                      {config.tipo === "recomendados" && config.codigos && (
                        <div className="flex flex-column gap-1">
                          <span className="text-700">Productos destacados</span>
                          <div className="flex flex-wrap gap-1">
                            {config.codigos.split(",").map((cod) => (
                              <Tag
                                key={cod}
                                value={cod.trim()}
                                severity="secondary"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {editando && !loading && (
                <div className="col-12 md:col-6">
                  <div className="border-1 surface-border border-round p-4">
                    <div className="flex align-items-center justify-content-between mb-3">
                      <span className="text-xl font-semibold">
                        Editar configuración
                      </span>
                    </div>

                    <div className="flex flex-column gap-4">
                      <div>
                        <label className="text-700 block mb-2">
                          Tipo de productos
                        </label>
                        <SelectButton
                          value={draft.tipo}
                          options={opciones}
                          onChange={(e) => onCampoChange("tipo", e.value)}
                        />
                      </div>

                      <div>
                        <label className="text-700 block mb-2">
                          Límite de productos
                        </label>
                        <InputNumber
                          value={draft.limite}
                          onValueChange={(e) =>
                            onCampoChange("limite", e.value)
                          }
                          min={1}
                          showButtons
                          size={5}
                          tooltip="Número máximo de productos que se mostrarán."
                          tooltipOptions={{ position: "top" }}
                        />
                      </div>

                      {draft.tipo === "recomendados" && (
                        <div className="flex flex-column gap-2">
                          <label className="text-700 block">
                            Productos destacados
                          </label>
                          {draft.codigos && (
                            <div className="flex flex-wrap gap-1 mb-2">
                              {draft.codigos.split(",").map((cod, index, arr) => (
                                <div key={cod} className="flex align-items-center gap-1">
                                  <Button
                                    icon="pi pi-chevron-left"
                                    className="p-button-rounded p-button-text p-button-sm"
                                    onClick={() => moverCodigo(index, -1)}
                                    disabled={index === 0}
                                  />
                                  <Tag value={cod.trim()} severity="secondary" />
                                  <Button
                                    icon="pi pi-chevron-right"
                                    className="p-button-rounded p-button-text p-button-sm"
                                    onClick={() => moverCodigo(index, 1)}
                                    disabled={index === arr.length - 1}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                          <Button
                            icon="pi pi-search"
                            label={
                              draft.codigos
                                ? "Modificar productos"
                                : "Seleccionar productos"
                            }
                            severity="info"
                            outlined
                            onClick={() => setDialogVisible(true)}
                          />
                        </div>
                      )}

                      <div className="flex gap-2 justify-content-end">
                        <Button
                          label="Cancelar"
                          icon="pi pi-times"
                          severity="danger"
                          onClick={onCancelar}
                          disabled={guardando}
                        />
                        <Button
                          label="Guardar"
                          icon="pi pi-check"
                          severity="success"
                          onClick={onGuardar}
                          loading={guardando}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Destacados;
